import React from 'react';
import { useDispatch } from 'react-redux';
import { Dropdown } from '@cambridgeassessment/cambridge-ui';
import { Component, SamplingAlgorithm, BUSINESS_UNITS } from '@cambridgeassessment/lib-ee-commons';
import { DropdownContainer } from '../../Content.styles';
import { StatefulLink, StyledComponentHeaderText, StyledSamplingAlgorithmSelect } from './styles';
import * as thunks from '../../../../store/components/thunks';

interface AlgorithmDropdownData {
  key: SamplingAlgorithm;
  name: string;
}

const algorithmSelectOptions: AlgorithmDropdownData[] = [
  {
    key: SamplingAlgorithm.OcrGq,
    name: 'OCR GQ (SALGO)',
  },
  {
    key: SamplingAlgorithm.CiGq,
    name: 'CI GQ',
  },
  {
    key: SamplingAlgorithm.NoSampling,
    name: 'No sampling algorithm',
  },
];

interface Props {
  component: Component;
  disabled?: boolean;
}

export const SamplingAlgorithmDropdown = ({ component, disabled }: Props): JSX.Element => {
  const dispatch = useDispatch();

  const handleAlgorithmChange = (val: SamplingAlgorithm) => {
    dispatch(
      thunks.updateComponent(
        component._id,
        {
          ...component,
          samplingAlgorithm: val,
        },
        'samplingAlgorithm',
      ),
    );
  };

  const defaultAlgo =
    component.businessUnit && component.businessUnit.id === BUSINESS_UNITS.CI.id
      ? algorithmSelectOptions[1]
      : algorithmSelectOptions[0];

  const initialValue = algorithmSelectOptions.find((x) => x.key === component.samplingAlgorithm) ?? defaultAlgo;

  const renderSelectOption = (value: AlgorithmDropdownData): string => value.name;

  const renderHelper = (): React.ReactElement => (
    <div data-qa-id='sampling-algo-helper-text'>
      Please note that the sampling algorithm must have a start date added. You can do this in the{' '}
      <StatefulLink
        data-qa-id='salgo-component-deadlines'
        to={`/components/configure-component/${component._id}/deadlines`}
        disabled={disabled}
      >
        Deadlines for the component
      </StatefulLink>{' '}
      page.
    </div>
  );

  return (
    <>
      <StyledComponentHeaderText>Sampling algorithm</StyledComponentHeaderText>
      <StyledSamplingAlgorithmSelect>
        <DropdownContainer>
          <Dropdown<AlgorithmDropdownData>
            data={algorithmSelectOptions}
            renderOption={renderSelectOption}
            renderSelectedOption={renderSelectOption}
            dataQaId='sampling-algorithm-select'
            onChange={(value) => handleAlgorithmChange(value.key)}
            value={initialValue}
            placeholder='Please select a sampling algorithm'
            title='Select sampling algorithm'
            disabled={disabled}
            ariaLabel='sampling-algorithm-dropdown'
            renderHelper={renderHelper}
          />
        </DropdownContainer>
      </StyledSamplingAlgorithmSelect>
    </>
  );
};
